import React, { useEffect } from "react";
import {
    Button,
    Chip,
    ClickAwayListener,
    Fade,
    Grid,
    IconButton,
    InputAdornment,
    OutlinedInput,
    Popper,
    styled,
    Tooltip,
    Typography,
} from "@mui/material";
import { useTheme } from '@mui/material/styles';
import { ColumnsIcon, FunnelIcon, SearcherIcon } from "../../utils/icons";
import { SecondaryText, inputHeight, pageContentGap } from "../../theme";
import { Clear, Close, Delete, ExpandMore } from "@mui/icons-material";
import CatalogFilter from "./components/catalog_filter";
import Helper from "../../helpers";
import { getOperators } from "../table";
import ColumnsPopper from "./components/ColumnsPopper";
import moment from "moment";
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { useHistory, useLocation } from "react-router";
import { isSystemFilter, isCustomFilter } from '../saved_search/utils/filterManager';
import SavedSearchStorage from '../saved_search/services/SavedSearchStorage';

type FilterValue = string | number | boolean | null | string[] | [string, string] | Record<string, unknown>;

interface CatalogFilterItem {
    field?: string;
    operator?: string;
    value?: FilterValue;
    logic?: 'and' | 'or';
    filters?: CatalogFilterItem[];
    system?: boolean;
    static?: boolean;
}

interface ColumnOption {
    id: string;
    label: string;
    type?: string;
    hidden?: boolean;
    toggleable?: boolean;
    filterable?: boolean;
    defaultFilterOperator?: string;
    getOptions?: (allData: unknown[]) => Promise<Array<{ label: string; value?: unknown }>>;
}

interface TableState {
    filter: { filters: CatalogFilterItem[] } | null;
    allData?: unknown[];
}

interface TableExtensions {
    addFilters: (filters: CatalogFilterItem[]) => void;
    replaceFilters: (filters: CatalogFilterItem[]) => void;
    removeFilters: (filters: CatalogFilterItem[]) => void;
    onRemoveFilter: (filter: CatalogFilterItem) => void;
    isColumnHidden: (column: ColumnOption) => boolean;
    toggleColumn: (column: ColumnOption, hide: boolean) => void;
}

interface CatalogFiltersProps {
    config: { term?: string; columns: ColumnOption[]; [key: string]: unknown };
    setConfig: React.Dispatch<React.SetStateAction<{ term?: string; refresh?: boolean; columns?: ColumnOption[]; [key: string]: unknown }>>;
    table: { state: TableState; extensions: TableExtensions };
    style?: React.CSSProperties;
    Filters?: React.ReactNode;
    extensions?: React.ReactNode;
    renderFiltersAfter?: boolean;
    disableTermInUrl?: boolean;
    disableSearch?: boolean;
    disableDetailedFilters?: boolean;
    disableColumnSelector?: boolean;
    filterShowExceptions?: string[];
    filterNullExceptions?: string[];
    /** @deprecated Component now uses useHistory() internally; this prop is ignored. Remove from callers. */
    history?: unknown;
    /** @deprecated Component now uses useLocation() internally; this prop is ignored. Remove from callers. */
    location?: unknown;
}

const FiltersDetailed = styled(Grid)(({ theme }) => ({
    width: "750px",
    margin: "16px 0 0 0",
    padding: "8px 8px 12px 8px",
    background: "#FFFFFF",
    border: "1px solid #DCDCDC",
    borderRadius: "6px",

    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "flex-start",
    maxHeight: "80vh",
    overflowY: "auto",
}));

export const FilterBar = styled(Grid)<{ filterstyles?: React.CSSProperties }>(({ filterstyles }) => ({
    padding: "10px 12px",
    background: "#FFFFFF",
    borderRadius: "6px",
    overflow: "visible",
    ...filterstyles
}));
export const StyledFilterSelector = {
    background: "#F9F9F9 !important",
    borderRadius: "6px !important",
    height: "100%",
    // border: "1px solid #DCDCDC !important",
}
export const StyledFilterControl = {
    background: "#F9F9F9 !important",
}

// Check if a filter should be excluded from the detailed filter popover
const isExcludedFromPopover = (filter: CatalogFilterItem, filterShowExceptions: string[]) => {
    // System filters are always excluded
    if (isSystemFilter(filter)) return true;
    // Custom filters are excluded from popover
    if (isCustomFilter(filter)) return true;
    // Additional exceptions passed via props
    if (filter.field && filterShowExceptions.includes(filter.field)) return true;
    return false;
};

// Count user-visible filters (excluding system, custom, and compound filters)
const countUserVisibleFilters = (filters: CatalogFilterItem[] | null | undefined, filterShowExceptions: string[]) => {
    if (!filters || !Array.isArray(filters)) return 0;

    return filters.reduce((count, filter) => {
        // Skip compound filters entirely (they're marked with system: true)
        if (filter.filters && filter.system) return count;

        // Skip filters that should be excluded
        if (isExcludedFromPopover(filter, filterShowExceptions)) return count;

        // Count regular filters that have a field
        if (filter.field) return count + 1;

        return count;
    }, 0);
};

export const addFiltersToTable = async (filters: CatalogFilterItem[], table: { state: TableState; extensions: TableExtensions }) => {
    if (table.state.filter !== null) {
        let newFilters = [...table.state.filter.filters];
        filters.forEach((filter) => {
            let foundFilter = newFilters.find((newFilter) => {
                const valueMatch = Array.isArray(filter.value) && Array.isArray(newFilter.value)
                    ? JSON.stringify(filter.value) === JSON.stringify(newFilter.value)
                    : filter.value === newFilter.value;
                return newFilter.field === filter.field && newFilter.operator === filter.operator && valueMatch;
            });

            if (foundFilter === undefined) newFilters.push(filter);
        });

        if (newFilters.length > 0) {
            table.extensions.replaceFilters(newFilters);
        }
    } else {
        table.extensions.addFilters(filters);
    }
}


function CatalogFilters(props: CatalogFiltersProps) {
    const theme = useTheme();
    const history = useHistory();
    const location = useLocation();

    let filterShowExceptions = ["approvalStatus", "syncStatus", "errorMessage", "Status"];
    filterShowExceptions.push(...(props.filterShowExceptions || []));

    let filterNullExceptions = [""];
    filterNullExceptions.push(...(props.filterNullExceptions || []));

    const [detailedFiltersOpen, setDetailedFiltersOpen] = React.useState(false);
    const [detailedFiltersEl, setDetailedFiltersEl] = React.useState<HTMLElement | null>(null);
    const [columnsSelectorOpen, setColumnsSelectorOpen] = React.useState(false);
    const [columnsSelectorEl, setColumnsSelectorEl] = React.useState<HTMLElement | null>(null);

    const [detailedFilters, setDetailedFilters] = React.useState<CatalogFilterItem[]>([]);
    const [localTerm, setLocalTerm] = React.useState(props.config?.term || '');
    const pendingUpdateRef = React.useRef<number | null>(null);
    const isScrollingRef = React.useRef(false);
    const scrollTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

    React.useEffect(() => {
        setLocalTerm(props.config?.term || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.config?.term]);

    const debouncedSearch = React.useMemo(
        () => debounce((searchTerm: string) => {
            searchTerm = searchTerm.trim();

            if (pendingUpdateRef.current) {
                cancelAnimationFrame(pendingUpdateRef.current);
                pendingUpdateRef.current = null;
            }

            if (!props.disableTermInUrl) {
                new Helper().setTermInUrl(searchTerm, history, location);
                pendingUpdateRef.current = requestAnimationFrame(() => {
                    pendingUpdateRef.current = null;
                    props.setConfig((prevState) => ({ ...prevState, term: searchTerm, refresh: !prevState.refresh }));
                });
            } else {
                props.setConfig((prevState) => ({ ...prevState, term: searchTerm, refresh: !prevState.refresh }));
            }
        }, 500),

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.disableTermInUrl, props.setConfig, history, location]
    );

    React.useEffect(() => {
        return () => {
            debouncedSearch.cancel();
            if (pendingUpdateRef.current) {
                cancelAnimationFrame(pendingUpdateRef.current);
                pendingUpdateRef.current = null;
            }
        };
    }, [debouncedSearch]);

    const closeDetailedFilters = () => {
        setDetailedFiltersOpen(false);
    }

    const addFilters = async () => {
        closeDetailedFilters();

        let filtersToAdd = detailedFilters.filter((filter) => {
            if (filter.field === "" || filter.operator === "") return false;
            if (Array.isArray(filter.value)) {
                return filter.value.length > 0;
            }
            return filter.value !== "";
        });

        filtersToAdd = filtersToAdd.filter((filter, index) => {
            const firstOccurrenceIndex = filtersToAdd.findIndex(newFilter => {
                if (newFilter.field !== filter.field || newFilter.operator !== filter.operator) {
                    return false;
                }
                if (Array.isArray(filter.value) && Array.isArray(newFilter.value)) {
                    return JSON.stringify(filter.value) === JSON.stringify(newFilter.value);
                }
                return filter.value === newFilter.value;
            });
            return firstOccurrenceIndex === index;
        });

        if (props.table.state.filter === null) {
            props.table.extensions.addFilters(filtersToAdd);
            return;
        }

        let staticFilters = props.table.state.filter.filters
            .filter(filter => isExcludedFromPopover(filter, filterShowExceptions));
        let combinedFilters = [...staticFilters, ...cloneDeep(filtersToAdd)];

        props.table.extensions.replaceFilters(combinedFilters);
    }

    const deleteFilters = async (filters: CatalogFilterItem[]) => {
        if (props.table.state.filter !== null)
            if (filters.length === 1) {
                props.table.extensions.onRemoveFilter(filters[0]);
            } else if (filters.length > 1) {
                props.table.extensions.removeFilters(filters);
            }
    }

    const updateTerm = (newTerm?: string) => {
        let searchTerm = newTerm !== undefined ? newTerm : localTerm;
        searchTerm = searchTerm.trim();

        debouncedSearch.cancel();

        if (pendingUpdateRef.current) {
            cancelAnimationFrame(pendingUpdateRef.current);
            pendingUpdateRef.current = null;
        }

        if (!props.disableTermInUrl) {
            new Helper().setTermInUrl(searchTerm, history, location);
        }

        props.setConfig((prevState) => ({ ...prevState, term: searchTerm, refresh: !prevState.refresh }));
    }

    const renderChips = () => {
        let chips = props.table.state.filter.filters.map((filter, index) => {

            if (filter.value === null && !filterNullExceptions.includes(filter.field)) return null;
            if (filter.filters) return null;
            // Only hide system filters from chips
            if (isSystemFilter(filter)) return null;

            let column = props.config.columns.find((column) => column.id === filter.field);
            if (!column || column.hidden) return null;

            let operator = getOperators(column).find((operator) => operator.op === filter.operator) || { label: "" };

            let formatedValue: unknown = filter.value;
            if (filter.value === null) {
                formatedValue = <i>null</i>;
            } else if (filter.value === "") {
                formatedValue = <i>empty</i>;
            }

            if (column.type != null && column.type === "date") {
                if (Array.isArray(filter.value)) {
                    formatedValue = <>{moment(filter.value[0]).format("MM/DD/YYYY")}<span style={{ fontWeight: 'normal' }}> and </span>{moment(filter.value[1]).format("MM/DD/YYYY")}</>;
                } else {
                    if (typeof filter.value === 'string' || typeof filter.value === 'number' || filter.value instanceof Date) {
                        formatedValue = moment(filter.value).format("MM/DD/YYYY");
                    }
                }
            }
            if (column.type === "bool") formatedValue = `${filter.value}`;

            let tooltipTitle = "";
            let isSpecialFormat = false;
            if (Array.isArray(filter.value) && column.type !== "date") {
                const values = filter.value.map((v) => {
                    if (typeof v === 'object' && v !== null) {
                        const valueObj = v as { label?: string; value?: string | number | boolean };
                        return valueObj.label || valueObj.value || JSON.stringify(v);
                    }
                    return v;
                });
                if (values.length > 3) {
                    formatedValue = values;
                    isSpecialFormat = true;
                    tooltipTitle = `${column.label} ${operator.label}: ${values.join(", ")}`;
                } else {
                    formatedValue = values;
                    isSpecialFormat = true;
                }
            }

            const chipLabel = `${column.label} ${operator.label} : ${formatedValue}`;
            const shouldShowTooltip = tooltipTitle || chipLabel.length > 60;

            const chipContent = (
                <Chip
                    key={index}
                    sx={{ '& .MuiChip-label': { padding: "8px" } }}
                    variant={"outlined"}
                    style={{ maxWidth: "450px" }}
                    label={
                        <Grid container alignItems={"center"} style={{ gap: "6px" }} wrap="nowrap">
                            <Grid item style={{ overflow: "hidden", display: "flex", alignItems: "center" }}>
                                <Typography component="span" style={{ whiteSpace: "nowrap" }}>
                                    {column.label} {operator.label}:&nbsp;
                                </Typography>
                                {isSpecialFormat ? (
                                    <span style={{ fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {Array.isArray(formatedValue) && formatedValue.length > 3 ? (
                                            <>
                                                {formatedValue.slice(0, 2).join(", ")}
                                                <span style={{ fontWeight: "normal" }}> and </span>
                                                {formatedValue.length - 2}
                                                <span style={{ fontWeight: "normal" }}> more</span>
                                            </>
                                        ) : (
                                            (formatedValue as Array<string | number | boolean>).map((val, idx) => (
                                                <React.Fragment key={idx}>
                                                    {idx > 0 && <span style={{ fontWeight: "normal" }}> or </span>}
                                                    {val}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </span>
                                ) : (
                                    <Typography component="span" style={{ fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                        {formatedValue as React.ReactNode}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item>
                                <IconButton
                                    style={{ width: "16px", height: "16px", padding: 0 }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        deleteFilters([filter]);
                                    }}
                                    size="large">
                                    <Close style={{ fontSize: "16px" }} />
                                </IconButton>
                            </Grid>
                        </Grid>
                    }
                />
            );

            return shouldShowTooltip ? (
                <Tooltip key={index} title={tooltipTitle || chipLabel}>
                    {chipContent}
                </Tooltip>
            ) : chipContent;
        })

        chips = chips.filter(chip => chip !== null)
        if (chips.length === 0) return null;
        return (
            <Grid item container justifyContent={"flex-end"} alignItems={"center"} style={{ gap: "6px", margin: "8px 0 0 0", flexWrap: "wrap", flex: 1 }}>
                {chips}
                <Button
                    variant="text"
                    size="small"
                    onClick={() => {
                        // Clear table filters (preserving system filters)
                        const systemFilters = props.table.state.filter.filters.filter(f => isSystemFilter(f));
                        if (systemFilters.length > 0) {
                            props.table.extensions.replaceFilters(systemFilters);
                        } else {
                            props.table.extensions.replaceFilters([]);
                        }

                        // Also clear the saved search from URL
                        SavedSearchStorage.updateURLWithSearchId(null);
                    }}
                    startIcon={<Clear />}
                    style={{
                        height: '32px',
                        textTransform: 'none'
                    }}>
                    Clear All
                </Button>
            </Grid>
        );
    }

    /* Added because in some instances with small/zoomed in screens opening dropdown in the popover would close the popover */
    React.useEffect(() => {
        if (detailedFiltersOpen) {
            const handleScroll = () => {
                isScrollingRef.current = true;
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = setTimeout(() => {
                    isScrollingRef.current = false;
                }, 150); // Small delay after scroll ends
            };

            window.addEventListener('scroll', handleScroll, true);
            document.addEventListener('scroll', handleScroll, true);

            return () => {
                window.removeEventListener('scroll', handleScroll, true);
                document.removeEventListener('scroll', handleScroll, true);
                clearTimeout(scrollTimeoutRef.current);
            };
        }
    }, [detailedFiltersOpen]);

    useEffect(() => {
        setDetailedFiltersOpen(false);

        if (props.table.state.filter != null) {
            // Get only user-editable filters (exclude system and custom filters from popover)
            let cleanedFilters = props.table.state.filter.filters.filter(filter =>
                filter.field && !isExcludedFromPopover(filter, filterShowExceptions)
            );

            setDetailedFilters(cloneDeep(cleanedFilters));
        } else {
            setDetailedFilters([]);
        }

        if (!props.disableTermInUrl) {
            let urlTerm = new Helper().getTermFromUrl();
            if (urlTerm)
                props.setConfig((prevState) => {
                    return ({ ...prevState, term: urlTerm })
                });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.table.state])

    return (
        <FilterBar container alignItems={"center"} filterstyles={{ ...props.style, width: "100%", display: "flex" }}>
            <div style={{ flex: 1 }}>
                <Grid item container style={{ gap: "0.5em", flex: 1 }}>
                    {!props.renderFiltersAfter && props.Filters}

                    <Grid item container size={(props.Filters && !props.renderFiltersAfter) ? undefined : 12}
                        alignItems={"center"}
                        style={{ gap: "8px", minWidth: "fit-content", flex: 1, justifyContent: props.renderFiltersAfter ? "space-between" : props.Filters ? "flex-end" : "space-between" }}
                    >
                        {props.extensions}

                        {!props.disableSearch ? (
                            <OutlinedInput placeholder={"Search..."} value={localTerm}
                                onChange={(e) => {
                                    setLocalTerm(e.target.value);
                                    debouncedSearch(e.target.value);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                        e.preventDefault();
                                        updateTerm(localTerm);
                                    }
                                }}
                                sx={{
                                    height: inputHeight,
                                    "& .MuiOutlinedInput-notchedOutline": { borderRadius: "6px" },
                                    flex: 1,
                                    minWidth: "200px",
                                    maxWidth: "400px"
                                }}
                                startAdornment={
                                    <InputAdornment style={{ cursor: "pointer", marginRight: 0 }} position="start"
                                        onClick={() => updateTerm(localTerm)}>
                                        <SearcherIcon />
                                    </InputAdornment>
                                }
                                endAdornment={
                                    <>
                                        {localTerm && (
                                            <InputAdornment style={{ cursor: "pointer" }} position="end"
                                                onClick={() => {
                                                    setLocalTerm("");
                                                    debouncedSearch.cancel();
                                                    updateTerm("");
                                                }}>
                                                <Clear
                                                    style={{ width: "20px", height: "20px", opacity: "0.8" }} />
                                            </InputAdornment>
                                        )}
                                    </>
                                }
                            />
                        ) : <div style={{ flex: 1 }} />}

                        <div>
                            {!props.disableDetailedFilters && (
                                <Button variant="outlined"
                                    aria-label="Filters"
                                    aria-haspopup="dialog"
                                    aria-expanded={detailedFiltersOpen}
                                    title="Filters"
                                    style={{
                                        padding: "0 8px 0 12px",
                                        marginRight: "4px",
                                        background: "#F9F9F9",
                                        borderColor: detailedFiltersOpen ? (theme.palette.tertiary.main) : "#DCDCDC"
                                    }}
                                    onClick={(e) => {
                                        if (!detailedFiltersOpen) {
                                            if (props.table.state.filter != null) {
                                                let cleanedFilters = props.table.state.filter.filters.filter(filter =>
                                                    filter.field && !isExcludedFromPopover(filter, filterShowExceptions)
                                                );

                                                setDetailedFilters(cloneDeep(cleanedFilters));
                                            } else {
                                                setDetailedFilters([]);
                                            }
                                        }
                                        setDetailedFiltersEl(e.currentTarget);
                                        setDetailedFiltersOpen(!detailedFiltersOpen);
                                    }}
                                >
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: pageContentGap,
                                        color: detailedFiltersOpen ? (theme.palette.tertiary.main) : "black",
                                    }}>
                                        {props.table.state.filter !== null &&
                                            countUserVisibleFilters(props.table.state.filter.filters, filterShowExceptions) > 0 && (
                                                <Typography style={{
                                                    width: "18px",
                                                    height: "18px",
                                                    fontSize: "14px",
                                                    color: "white",
                                                    background: theme.palette.tertiary.main,
                                                    borderRadius: "6px",
                                                }}>
                                                    {countUserVisibleFilters(props.table.state.filter.filters, filterShowExceptions)}
                                                </Typography>
                                            )}
                                        <FunnelIcon style={{ width: "18px" }} />
                                        <ExpandMore style={{ width: "18px" }} />
                                    </div>
                                </Button>
                            )}

                            {!props.disableColumnSelector && (
                                <Button variant="outlined"
                                    aria-label="Columns"
                                    aria-haspopup="dialog"
                                    aria-expanded={columnsSelectorOpen}
                                    title="Columns"
                                    style={{
                                        padding: "0 8px 0 12px",
                                        marginLeft: "4px",
                                        background: "#F9F9F9",
                                        borderColor: columnsSelectorOpen ? (theme.palette.tertiary.main) : "#DCDCDC"
                                    }}
                                    onClick={(e) => {
                                        setColumnsSelectorOpen(!columnsSelectorOpen);
                                        setColumnsSelectorEl(e.currentTarget);
                                    }}
                                >
                                    <div style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                        color: columnsSelectorOpen ? (theme.palette.tertiary.main) : "black",
                                    }}>
                                        <ColumnsIcon />
                                        <ExpandMore />
                                    </div>
                                </Button>
                            )}
                        </div>
                    </Grid>

                    {props.renderFiltersAfter && props.Filters}
                </Grid>

                {props.table.state.filter != null && props.table.state.filter.filters.length > 0 && renderChips()}
            </div>
            <ColumnsPopper open={columnsSelectorOpen} anchorEl={columnsSelectorEl} setOpen={setColumnsSelectorOpen}
                setAnchorEl={setColumnsSelectorEl} config={props.config} table={props.table} />
            <Popper open={detailedFiltersOpen} anchorEl={detailedFiltersEl} transition
                placement={"bottom-end"}
                style={{ zIndex: 1400 }}>
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={(event) => {
                        // Ignore if clicking on react-select elements or Material-UI select elements
                        const target = event.target as HTMLElement | null;
                        if (target?.closest('.enhanced-select__menu') ||
                            target?.closest('.enhanced-select__menu-portal') ||
                            target?.closest('[class*="react-select"]') ||
                            target?.closest('.MuiPopover-root') ||
                            target?.closest('.MuiAutocomplete-popper')) {
                            return;
                        }
                        setDetailedFiltersOpen(false);
                    }}>
                        <Fade {...TransitionProps} timeout={350}>
                            <FiltersDetailed container
                                style={{ gap: detailedFilters.length > 0 ? pageContentGap : "0px" }}>
                                <Grid item container justifyContent={"space-between"}>
                                    <SecondaryText
                                        style={{ fontSize: "16px" }}><b>Filters</b></SecondaryText>
                                </Grid>

                                {detailedFilters.length !== 0 ? (
                                    <Grid item container style={{ gap: pageContentGap, width: "100%" }}>
                                        {detailedFilters.map((filter, index) => {
                                            return (
                                                <Grid item container justifyContent={"flex-start"} key={index}
                                                    alignItems={"center"} style={{ gap: "10px", fontSize: "14px", width: "100%", flexWrap: "nowrap" }}>
                                                    <CatalogFilter detailedFilters={detailedFilters}
                                                        setDetailedFilters={setDetailedFilters}
                                                        filter={filter} index={index}
                                                        columns={props.config.columns}
                                                        table={props.table}
                                                    />
                                                    <IconButton
                                                        onClick={() => {
                                                            let newFilters = [...detailedFilters];
                                                            newFilters.splice(index, 1);
                                                            setDetailedFilters(newFilters);
                                                        }}
                                                        size="large">
                                                        <Delete />
                                                    </IconButton>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                ) : (
                                    <Grid item container>
                                        <Typography style={{ fontSize: "14px", color: "#7F7D83", fontStyle: "italic" }}>
                                            No editable filters. Click '+ Add filter' to create a new filter.
                                        </Typography>
                                    </Grid>
                                )}

                                <Grid item container justifyContent={"flex-end"} style={{ width: '100%' }}>
                                    <Button variant={"text"}
                                        style={{ textTransform: "none", color: theme.palette.tertiary.main }}
                                        onClick={() => {
                                            setDetailedFilters([...detailedFilters, {
                                                field: "",
                                                operator: "",
                                                value: "",
                                            }]);
                                        }}
                                    >
                                        + Add filter
                                    </Button>

                                    <Button variant={"text"}
                                        style={{ textTransform: "none", color: theme.palette.tertiary.main }}
                                        onClick={() => addFilters()}
                                    >
                                        Apply
                                    </Button>
                                </Grid>
                            </FiltersDetailed>
                        </Fade>
                    </ClickAwayListener>
                )}
            </Popper>
        </FilterBar>
    );
}

export default CatalogFilters;
